import { Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import type { useWorkspaceUsersQuery } from '@/modules/users/queries/useWorkspaceUsersQuery';
import type { WorkspaceApi, WorkspaceUserApi } from '@/modules/users/types';

interface WorkspaceUserListProps {
  /** Opens the invite; the "just you" row offers it as its next step. */
  onInvite: () => void;
  workspace: WorkspaceApi | undefined;
  workspaceUsersQuery: ReturnType<typeof useWorkspaceUsersQuery>;
}

/**
 * Every exclusive Hiring Manager Workspace entry, oldest first, with whether
 * they have signed in yet.
 */
export function WorkspaceUserList({
  onInvite,
  workspace,
  workspaceUsersQuery,
}: WorkspaceUserListProps) {
  const currentUserId = useCurrentUserQuery().data?.id;
  const users = workspace?.users;

  if (workspaceUsersQuery.isLoading) {
    return (
      <div className="divide-border flex max-w-[720px] flex-col divide-y border-y">
        <Skeleton className="my-3 h-7 w-full" />
        <Skeleton className="my-3 h-7 w-full" />
      </div>
    );
  }

  if (workspaceUsersQuery.isError || !users) {
    return (
      <HMLoadError
        description="We could not load your workspace members. Check your connection and try again."
        isRetrying={workspaceUsersQuery.isFetching}
        onRetry={() => void workspaceUsersQuery.refetch()}
        title="Workspace members could not be loaded"
      />
    );
  }

  // One member is the normal state for a fresh workspace: the owner. Say what
  // inviting someone gets them rather than leaving a single row unexplained.
  const isAlone = users.length <= 1;
  const seatCapacity = workspace.seatCapacity;
  const isAtSeatCapacity =
    seatCapacity.limit != null && seatCapacity.used >= seatCapacity.limit;

  return (
    <div className="flex max-w-[720px] flex-col">
      <ul
        className={cn(
          'divide-border flex flex-col divide-y',
          isAlone ? 'border-t' : 'border-y',
        )}
      >
        {users.map((user) => (
          <MemberRow
            isYou={user.id === currentUserId}
            key={user.id}
            user={user}
          />
        ))}
      </ul>

      {isAlone && (
        <PortalEmptyState
          action={
            isAtSeatCapacity
              ? undefined
              : { label: 'Invite teammate', onClick: onInvite }
          }
          description="Invite teammates to review candidates with you in Slack."
          icon={Users}
          size="row"
          title="It's just you so far."
        />
      )}
    </div>
  );
}

function MemberRow({
  user,
  isYou,
}: {
  user: WorkspaceUserApi;
  isYou: boolean;
}) {
  const email = user.email ?? '';
  return (
    <li className="flex items-center justify-between gap-4 py-3">
      <span className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden
          className="bg-muted grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-neutral-600 uppercase"
        >
          {email.charAt(0) || '?'}
        </span>
        <span className="truncate text-[13px]">
          {email}
          {isYou && <span className="ml-1.5 text-neutral-500">You</span>}
        </span>
      </span>
      <StatusDot status={user.inviteStatus} />
    </li>
  );
}

function StatusDot({ status }: { status: WorkspaceUserApi['inviteStatus'] }) {
  return (
    <span className="text-muted-foreground inline-flex h-5 shrink-0 items-center gap-1.5 text-xs font-medium">
      <span
        className={cn(
          'size-1.5 rounded-full',
          status === 'Active' ? 'bg-semantic-560' : 'bg-neutral-500',
        )}
      />
      {status === 'Active' ? 'Active' : 'Not signed in yet'}
    </span>
  );
}
